import { useLazyQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import type { CareerRole_PathwayLevel as PathwayLevel } from '__generated__/graphql-types';

import CareerRolesOnCAQuery from 'bundles/unified-career-academy/queries/CareerRolesQuery.graphql';
import type {
  CareerRoleOnCaFragment,
  CareerRolesOnCaQueryVariables,
  CareerRolesOnCaQuery as CarereRolesOnCAQueryType,
  PathwaysCollectionDataFragment,
  SpecializationPathwayFragment,
} from 'bundles/unified-career-academy/queries/__generated__/CareerRolesQuery';
import { getPathwayLevel } from 'bundles/unified-common/hooks/useCareerRolesCollection';

export type SpecializationPathwayType = SpecializationPathwayFragment & {
  proficiencyLevel?: PathwayLevel;
};

export type CareerRole = Omit<CareerRoleOnCaFragment, 'pathways'> & {
  pathways: Omit<PathwaysCollectionDataFragment, 'entities'> & {
    entities?: Array<SpecializationPathwayType>;
  };
};

export const normalizeCareerRolesData = (content?: CareerRoleOnCaFragment[] | null): CareerRole[] | undefined => {
  if (!content?.length) {
    return undefined;
  }

  return content.map((role) => {
    return {
      ...role,
      pathways: {
        ...role?.pathways,
        id: role?.pathways?.id as string,
        entities: role?.pathways?.entities?.elements
          .map((pathway) => {
            if (
              pathway?.__typename !== 'ProductCard_ProductCard' ||
              pathway?.productTypeAttributes?.__typename !== 'ProductCard_Specialization'
            ) {
              return null;
            }

            const product = pathway?.productTypeAttributes?.canonical;

            return {
              ...product,
              proficiencyLevel: getPathwayLevel(product?.difficultyLevel),
            };
          })
          .filter(Boolean),
      },
    };
  });
};

export function useCareerRolesDataLazy({ onCompleted }: { onCompleted?: (data: CareerRole[] | undefined) => void }) {
  const [fetchMoreCareerRolesData, { data, loading }] = useLazyQuery<
    CarereRolesOnCAQueryType,
    CareerRolesOnCaQueryVariables
  >(CareerRolesOnCAQuery, {
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all' as const,
    context: { clientName: 'gatewayGql' },
    onCompleted: (response) => {
      const normalizedData = normalizeCareerRolesData(response?.PathwaySkillCareerRoles?.queryRolesByIds);
      onCompleted?.(normalizedData);
    },
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: `Error fetching Pathway career roles data"`,
        },
      }),
  });

  return {
    fetchMoreCareerRolesData,
    data: normalizeCareerRolesData(data?.PathwaySkillCareerRoles?.queryRolesByIds),
    loading,
  };
}

export default useCareerRolesDataLazy;
